import React from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient, sendRpcWithKeepalive } from '../data/supabase';
import useSessionStore from '../state/session';

type Json = any;
type PendingPrefPatch = {
  value: Json;
  valueKey: string;
};
type PendingPrefEntry = [string, PendingPrefPatch];

/** Merges changed paths into ui_prefs on the server in one statement (20261002202748_user_ui_prefs_patch.sql). */
const PATCH_PREFS_FUNCTION = 'patch_user_ui_prefs';
// Set once a save finds no patch_user_ui_prefs (a database from before that migration): the page then
// saves with the old read-and-write and sends nothing while it unloads.
let patchPrefsFunctionMissing = false;

function getPath(obj: any, path: string, fallback: any){
  try {
    const parts = path.split('.');
    let cur = obj;
    for(const p of parts){ if(cur == null) return fallback; cur = cur[p]; }
    return cur == null ? fallback : cur;
  } catch { return fallback; }
}

/** The stored value at a path. Unlike getPath, a stored null stays null instead of reading as missing. */
function readPath(obj: unknown, path: string): unknown {
  let cur = obj;
  for (const part of path.split('.')) {
    if (cur == null) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function setPath(obj: any, path: string, value: any){
  const parts = path.split('.');
  const out = { ...(obj||{}) };
  let cur = out;
  for(let i=0;i<parts.length-1;i++){
    const k = parts[i];
    cur[k] = { ...(cur[k]||{}) };
    cur = cur[k];
  }
  cur[parts[parts.length-1]] = value;
  return out;
}

function stringifyPrefValue(value: Json): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function readLocalPrefs(lsKey: string): Json {
  try {
    const raw = localStorage.getItem(lsKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeLocalPrefs(lsKey: string, prefs: Json): void {
  try {
    localStorage.setItem(lsKey, JSON.stringify(prefs || {}));
  } catch {
    // localStorage is only a cache/mirror for the server-backed prefs.
  }
}

function applyPrefPatches(base: Json, patches: Iterable<[string, PendingPrefPatch]>): Json {
  let next = base && typeof base === 'object' && !Array.isArray(base) ? base : {};
  for (const [path, patch] of patches) {
    next = setPath(next, path, patch.value);
  }
  return next;
}

/** The server function's arguments. A value of undefined is dropped by JSON, which removes the key there too. */
function patchPrefsArgs(profileId: string, entries: PendingPrefEntry[]) {
  return {
    p_profile_id: profileId,
    p_patches: entries.map(([path, patch]) => ({ path: path.split('.'), value: patch.value })),
  };
}

/** Saves the changed paths: merged on the server, or read and written back where that function is missing. */
async function savePrefPatches(client: SupabaseClient, profileId: string, entries: PendingPrefEntry[]): Promise<void> {
  if (!patchPrefsFunctionMissing) {
    const { error } = await client.rpc(PATCH_PREFS_FUNCTION, patchPrefsArgs(profileId, entries));
    if (!error) return;
    if (error.code !== 'PGRST202') throw error;
    patchPrefsFunctionMissing = true;
  }
  const { data, error: loadError } = await client
    .from('user_profiles')
    .select('ui_prefs')
    .eq('id', profileId)
    .maybeSingle();
  if (loadError) throw loadError;
  const serverPrefs = (data as any)?.ui_prefs || {};
  const toSave = applyPrefPatches(serverPrefs, entries);
  if (JSON.stringify(toSave) !== JSON.stringify(serverPrefs)) {
    const { error: updateError } = await client.from('user_profiles').update({ ui_prefs: toSave }).eq('id', profileId);
    if (updateError) throw updateError;
  }
}

/**
 * Recursively merge two plain objects.  Server keys win; base keys that the
 * server does not have are preserved.  Arrays and non-object values are NOT
 * recursively merged — the server value wins outright, which is the correct
 * semantic for arrays like `savedViews`.
 */
function deepMerge(base: any, server: any): any {
  if (server == null) return base;
  if (typeof server !== 'object' || Array.isArray(server)) return server;
  if (base == null || typeof base !== 'object' || Array.isArray(base)) return server;
  const out: any = { ...base };
  for (const k of Object.keys(server)) {
    out[k] = deepMerge(base[k], server[k]);
  }
  return out;
}

/**
 * Persist lightweight UI preferences per user.
 * - Primary store: user_profiles.ui_prefs (jsonb) for cross-device persistence
 * - Fallback: localStorage (keyed by profileId when available)
 *
 * Saves are debounced (700 ms) and send only the changed paths. A page that goes away (pagehide) or an
 * instance that unmounts sends what is pending at once with fetch keepalive, so a full page load
 * cannot cancel it; a hidden tab saves normally. See docs/ui-preferences.md.
 */
export function useUserPrefs(supabase?: SupabaseClient, enabled = true){
  const client = supabase ?? getSupabaseClient();
  const profileId = useSessionStore(s=>s.profileId);
  const [prefs, setPrefs] = React.useState<Json>({});
  // localStorage key
  const lsKey = React.useMemo(()=> `ui_prefs:${profileId || 'anon'}`, [profileId]);
  // `loaded` means loaded for the current profile and source. Before the session resolves the
  // instance loads the anonymous copy, and a disabled instance only the local mirror; when the
  // profile id arrives or the instance is enabled it is false again until that read lands, so a
  // page never restores from (or saves over) a copy that is not the profile's.
  const loadKey = `${lsKey}:${enabled ? 'server' : 'local'}`;
  const [loadedKey, setLoadedKey] = React.useState<string | null>(null);
  const loaded = loadedKey === loadKey;

  // Save coordination
  const saveTimerRef = React.useRef<any>(null);
  const savingRef = React.useRef(false);
  const pendingPatchesRef = React.useRef<Map<string, PendingPrefPatch>>(new Map());
  // Every change made while the current load is in flight. The load replaces state with a
  // localStorage copy and a server read taken before those changes, so it re-applies them;
  // otherwise a click made during the read is undone on screen even after it saved.
  const changesDuringLoadRef = React.useRef<Map<string, PendingPrefPatch>>(new Map());

  const flushSaveRef = React.useRef<() => Promise<void> | void>(() => {});
  const sendOnExitRef = React.useRef<() => void>(() => {});

  const scheduleSave = React.useCallback(() => {
    // debounce multiple rapid updates into a single save
    if(saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      flushSaveRef.current();
    }, 700);
  }, []);

  // A patch is cleared once a save of it has answered, unless it changed again in the meantime.
  function acknowledge(entries: PendingPrefEntry[]){
    for (const [path, patch] of entries) {
      if (pendingPatchesRef.current.get(path)?.valueKey === patch.valueKey) pendingPatchesRef.current.delete(path);
    }
  }

  async function flushSave(){
    if(saveTimerRef.current){ clearTimeout(saveTimerRef.current); saveTimerRef.current = null; }
    if (!enabled) {
      pendingPatchesRef.current.clear();
      return;
    }
    if(!profileId) {
      pendingPatchesRef.current.clear();
      return; // no server write for anon/local-only
    }
    const pendingEntries = Array.from(pendingPatchesRef.current.entries());
    if(pendingEntries.length === 0) return;
    if(savingRef.current){
      // try again shortly after current save finishes
      saveTimerRef.current = setTimeout(() => {
        flushSaveRef.current();
      }, 500);
      return;
    }
    savingRef.current = true;
    try{
      await savePrefPatches(client, profileId, pendingEntries);
      acknowledge(pendingEntries);
    } catch{
      // Keep pending patches; they will retry on the next mutation or flush.
    } finally {
      savingRef.current = false;
    }
  }

  // Sends every pending patch in one fetch keepalive request, issued before this returns, so it is
  // delivered even when the page is going away. If it does not answer OK (an expired token, a
  // database without the function) the normal save retries, for as long as the page is still there.
  function sendOnExit(){
    if (!enabled || !profileId || patchPrefsFunctionMissing) {
      flushSaveRef.current();
      return;
    }
    const pendingEntries = Array.from(pendingPatchesRef.current.entries());
    if (pendingEntries.length === 0) return;
    let request: Promise<Response> | null = null;
    try {
      request = sendRpcWithKeepalive(PATCH_PREFS_FUNCTION, patchPrefsArgs(profileId, pendingEntries));
    } catch {
      request = null;
    }
    if (!request) {
      flushSaveRef.current();
      return;
    }
    request.then(
      (response) => { if (response.ok) acknowledge(pendingEntries); else flushSaveRef.current(); },
      () => { flushSaveRef.current(); },
    );
  }

  // Keep refs that always point at the latest functions so that the unmount cleanup and the page
  // listeners never invoke a stale closure.
  flushSaveRef.current = flushSave;
  sendOnExitRef.current = sendOnExit;

  // Load from server (if possible), otherwise fallback to localStorage
  React.useEffect(()=>{
    let active = true;
    changesDuringLoadRef.current = new Map();
    if (!enabled) {
      const base = readLocalPrefs(lsKey);
      setPrefs(base || {});
      setLoadedKey(loadKey);
      return () => {
        active = false;
        if(saveTimerRef.current) clearTimeout(saveTimerRef.current);
      };
    }
    (async ()=>{
      try {
        // Start with localStorage baseline
        let base: Json = {};
        base = readLocalPrefs(lsKey);

        if(profileId){
          const { data, error } = await client.from('user_profiles').select('ui_prefs').eq('id', profileId).maybeSingle();
          if (error) throw error;
          const server = (data as any)?.ui_prefs || {};
          // Deep merge: server keys win, but base-only sub-keys are preserved; changes made
          // while the read was in flight win over both.
          const merged = applyPrefPatches(deepMerge(base, server), changesDuringLoadRef.current.entries());
          if(active){ setPrefs(merged); setLoadedKey(loadKey); }
          writeLocalPrefs(lsKey, merged);
        } else {
          if(active){ setPrefs(base || {}); setLoadedKey(loadKey); }
        }
      } catch {
        // fallback to local only
        const base = applyPrefPatches(readLocalPrefs(lsKey), changesDuringLoadRef.current.entries());
        if(active){ setPrefs(base || {}); setLoadedKey(loadKey); }
      }
    })();
    return ()=>{
      active = false;
      if(saveTimerRef.current) clearTimeout(saveTimerRef.current);
      // Send any pending change now, in a request a following full page load cannot cancel.
      sendOnExitRef.current();
    };
  }, [client, enabled, profileId, lsKey, loadKey]);

  // A full page load (reload, leaving the app, closing the tab) cancels every awaited request, so
  // pagehide sends the pending changes with keepalive. A tab that only becomes hidden saves normally,
  // which covers phones that discard a hidden tab without a pagehide.
  React.useEffect(()=>{
    const onPageHide = () => { sendOnExitRef.current(); };
    const onVisibilityChange = () => { if (document.visibilityState === 'hidden') flushSaveRef.current(); };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  const get = React.useCallback(<T,>(path: string, fallback: T): T => {
    return getPath(prefs, path, fallback) as T;
  }, [prefs]);

  const set = React.useCallback((path: string, value: any) => {
    const valueKey = stringifyPrefValue(value);
    setPrefs((prev: Json) => {
      // Compare with the stored value itself: a stored null must equal a null being set, or every
      // echo of a null (AppShell's active site, on every page load) would queue a save.
      const currentValueKey = stringifyPrefValue(readPath(prev, path));
      if (currentValueKey === valueKey) {
        const localCurrent = readLocalPrefs(lsKey);
        const localValueKey = stringifyPrefValue(readPath(localCurrent, path));
        if (localValueKey !== valueKey) {
          writeLocalPrefs(lsKey, setPath(localCurrent, path, value));
        }
        if (pendingPatchesRef.current.get(path)?.valueKey === valueKey) {
          scheduleSave();
        }
        return prev;
      }
      const next = setPath(prev, path, value);
      // Mirror this path into localStorage without clobbering paths written by
      // other hook instances.
      const localNext = setPath(readLocalPrefs(lsKey), path, value);
      writeLocalPrefs(lsKey, localNext);
      changesDuringLoadRef.current.set(path, { value, valueKey });
      if (!enabled) return next;
      pendingPatchesRef.current.set(path, {
        value,
        valueKey,
      });
      scheduleSave();
      return next;
    });
  }, [enabled, lsKey, scheduleSave]);

  const flush = React.useCallback(() => {
    flushSaveRef.current();
  }, []);

  return { prefs, loaded, get, set, flush };
}
